import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressChart from '../progress-analytics/components/ProgressChart';
import MetricCard from '../progress-analytics/components/MetricCard';

const WorkoutAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [completedWorkouts, setCompletedWorkouts] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState('30d');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadAnalyticsData();
    }
  }, [user, currentTenant, timeframe]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');

      const daysAgo = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const { data: workouts, error: workoutsError } = await supabase
        ?.from('workout_completion_logs')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.not('completed_at', 'is', null)
        ?.gte('completed_at', startDate?.toISOString())
        ?.order('completed_at', { ascending: false });

      if (workoutsError) throw workoutsError;

      const { data: logs, error: logsError } = await supabase
        ?.from('workout_logs')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('completed_at', startDate?.toISOString())
        ?.order('completed_at', { ascending: true });

      if (logsError) throw logsError;

      setCompletedWorkouts(workouts || []);
      setWorkoutLogs(logs || []);
    } catch (err) {
      setError(err?.message || 'Failed to load analytics data');
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    const totalWorkouts = completedWorkouts?.length;
    const totalSets = completedWorkouts?.reduce((sum, w) => sum + (w?.completed_sets || 0), 0);
    const totalMinutes = completedWorkouts?.reduce((sum, w) => sum + (w?.duration_minutes || 0), 0);
    const totalCalories = completedWorkouts?.reduce((sum, w) => sum + (w?.calories_burned || 0), 0);

    const previousPeriodStart = new Date();
    const daysAgo = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
    previousPeriodStart?.setDate(previousPeriodStart?.getDate() - (daysAgo * 2));
    const currentPeriodStart = new Date();
    currentPeriodStart?.setDate(currentPeriodStart?.getDate() - daysAgo);

    const previousWorkouts = completedWorkouts?.filter(w => {
      const date = new Date(w?.completed_at);
      return date >= previousPeriodStart && date < currentPeriodStart;
    });

    const workoutChange = previousWorkouts?.length > 0
      ? Math.round(((totalWorkouts - previousWorkouts?.length) / previousWorkouts?.length) * 100)
      : 0;

    return {
      totalWorkouts,
      totalSets,
      totalMinutes,
      totalCalories,
      workoutChange
    };
  };

  const getProgressChartData = () => {
    const groupedByDate = {};
    
    completedWorkouts?.forEach(workout => {
      const date = new Date(workout?.completed_at);
      const dateKey = date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      if (!groupedByDate?.[dateKey]) {
        groupedByDate[dateKey] = {
          name: dateKey,
          workouts: 0,
          sets: 0,
          minutes: 0,
          calories: 0
        };
      }
      
      groupedByDate[dateKey].workouts += 1;
      groupedByDate[dateKey].sets += workout?.completed_sets || 0;
      groupedByDate[dateKey].minutes += workout?.duration_minutes || 0;
      groupedByDate[dateKey].calories += workout?.calories_burned || 0;
    });

    return Object.values(groupedByDate)?.reverse();
  };

  const getPersonalRecords = () => {
    const records = {};

    workoutLogs?.forEach(log => {
      const exerciseName = log?.exercise_name;
      const weight = log?.weight || 0;

      if (!records?.[exerciseName] || weight > records?.[exerciseName]?.weight) {
        records[exerciseName] = {
          exercise: exerciseName,
          weight,
          reps: log?.reps,
          date: new Date(log?.completed_at)?.toLocaleDateString()
        };
      }
    });

    return Object.values(records)?.slice(0, 5);
  };

  const metrics = calculateMetrics();
  const progressData = getProgressChartData();
  const personalRecords = getPersonalRecords();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">Loading analytics...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Workout Analytics Dashboard</h1>
              <p className="text-muted-foreground">
                Track your progress, analyze performance, and achieve your fitness goals
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={timeframe === '7d' ? 'default' : 'outline'}
                onClick={() => setTimeframe('7d')}
                size="sm"
              >
                7 Days
              </Button>
              <Button
                variant={timeframe === '30d' ? 'default' : 'outline'}
                onClick={() => setTimeframe('30d')}
                size="sm"
              >
                30 Days
              </Button>
              <Button
                variant={timeframe === '90d' ? 'default' : 'outline'}
                onClick={() => setTimeframe('90d')}
                size="sm"
              >
                90 Days
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-error">Error loading analytics</p>
              <p className="text-sm text-error/80 mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            icon="Dumbbell"
            label="Total Workouts"
            value={metrics?.totalWorkouts?.toString()}
            change={metrics?.workoutChange > 0 ? `+${metrics?.workoutChange}%` : `${metrics?.workoutChange}%`}
            changeType={metrics?.workoutChange >= 0 ? 'positive' : 'negative'}
            trend={metrics?.workoutChange >= 0 ? 'up' : 'down'}
            color="var(--color-accent)"
          />
          <MetricCard
            icon="Layers"
            label="Total Sets"
            value={metrics?.totalSets?.toString()}
            change=""
            color="var(--color-primary)"
          />
          <MetricCard
            icon="Clock"
            label="Training Time"
            value={Math.floor(metrics?.totalMinutes / 60)?.toString()}
            unit=" hrs"
            change=""
            color="var(--color-success)"
          />
          <MetricCard
            icon="Flame"
            label="Calories Burned"
            value={(metrics?.totalCalories / 1000)?.toFixed(1)}
            unit="k"
            change=""
            color="var(--color-warning)"
          />
        </div>

        {progressData?.length > 0 && (
          <div className="mb-8">
            <ProgressChart
              title="Workout Frequency & Volume"
              data={progressData}
              type="bar"
              dataKeys={[
                { dataKey: 'workouts', name: 'Workouts' },
                { dataKey: 'sets', name: 'Sets' }
              ]}
              height={300}
              colors={['var(--color-accent)', 'var(--color-primary)']}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {progressData?.length > 0 && (
            <ProgressChart
              title="Training Duration"
              data={progressData}
              type="line"
              dataKeys={[{ dataKey: 'minutes', name: 'Minutes' }]}
              height={250}
              colors={['var(--color-success)']}
            />
          )}
          {progressData?.length > 0 && (
            <ProgressChart
              title="Calories Burned"
              data={progressData}
              type="line"
              dataKeys={[{ dataKey: 'calories', name: 'Calories' }]}
              height={250}
              colors={['var(--color-warning)']}
            />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-card rounded-lg border border-border p-6 shadow-md">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Trophy" className="w-5 h-5 text-warning" />
              Personal Records
            </h3>
            {personalRecords?.length > 0 ? (
              <div className="space-y-3">
                {personalRecords?.map((record, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{record?.exercise}</p>
                      <p className="text-sm text-muted-foreground">{record?.date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary">{record?.weight} lbs</p>
                      <p className="text-sm text-muted-foreground">{record?.reps} reps</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                Complete workouts to track your personal records
              </p>
            )}
          </div>

          <div className="bg-card rounded-lg border border-border p-6 shadow-md">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Activity" className="w-5 h-5 text-accent" />
              Recent Workouts
            </h3>
            {completedWorkouts?.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {completedWorkouts?.slice(0, 5)?.map((workout) => (
                  <div key={workout?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{workout?.workout_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(workout?.completed_at)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-foreground">
                        {workout?.completed_exercises}/{workout?.total_exercises} exercises
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {workout?.duration_minutes} min • {workout?.calories_burned} cal
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No completed workouts yet. Start your first workout!
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/ai-workout-recommendations')}
            className="flex-1"
            iconName="Sparkles"
            iconPosition="left"
          >
            Get AI Recommendations
          </Button>
          <Button
            onClick={() => navigate('/workout-library')}
            variant="outline"
            className="flex-1"
            iconName="Library"
            iconPosition="left"
          >
            Browse Workout Library
          </Button>
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default WorkoutAnalyticsDashboard;